/**
 * English - `system`: the platform status card and the platform owner's status
 * page around it.
 *
 * Keys: `card.*` for `PlatformStatusCard`, `card.dependency.<status>` by the
 * probe's `up` / `down` / `unknown`; `page.*` for `PlatformStatusPage`. Service
 * names (FastAPI, PostgreSQL, Redis) and endpoint paths are not translated -
 * `page.intro` takes the paths as `{health}`, `{ready}` and `{meta}`.
 * Conventions: see `common.ts`.
 */
const system: Record<string, string> = {
  title: 'Platform status',

  'card.subtitle': 'Live checks against the NeonCarts API.',
  'card.all_systems_go': 'All systems go',
  'card.degraded': 'Degraded',
  'card.dependency.up': 'Operational',
  'card.dependency.down': 'Unreachable',
  'card.dependency.unknown': 'Checking…',
  'card.meta.version': 'Version',
  'card.meta.api': 'API',
  'card.meta.environment': 'Environment',

  'page.intro':
    'Live readings from {health}, {ready} and {meta}. Readiness checks PostgreSQL and Redis and returns 503 when either is unreachable.',
  'page.readiness.title': 'Readiness',
  'page.readiness.checking': 'Checking dependencies…',
  'page.readiness.unreachable': 'The API could not be reached. Is the backend running on port 8000?',
  'page.readiness.reported': 'Reported state: {status}.',
  'page.limits.title': 'API limits',
  'page.limits.description':
    'Pagination defaults to {page_size} items and is capped at {max_page_size}. Currency: {currency}.',
  'page.limits.loading': 'Loading platform metadata…',

  // Maintenance windows: the banner's countdown and active lines, the toast
  // when a save is refused, and the full-page screen. `{time}` is a clock time
  // in IST ("03:00 IST"), `{window}` a day and range ("Sat 02:00–03:00 IST").
  'maintenance.upcoming': 'Scheduled maintenance {window} · starts in {countdown} · {impact}',
  'maintenance.starting': 'Scheduled maintenance {window} is starting now · {impact}',
  'maintenance.impact_read_only': 'you can view everything, but saving will pause',
  'maintenance.impact_full': 'the dashboard will be unavailable',
  'maintenance.days_hours': '{days}d {hours}h',
  'maintenance.hours_minutes': '{hours}h {minutes}m',
  'maintenance.minutes_only': '{minutes}m',
  'maintenance.read_only_until': 'Read-only until {time} while we carry out maintenance. You can look around, but saving is paused.',
  'maintenance.read_only_shortly': 'Read-only while we carry out maintenance. Saving will be back shortly.',
  'maintenance.full_until': 'Maintenance in progress until {time}.',
  'maintenance.full_shortly': 'Maintenance in progress. We will be back shortly.',
  'maintenance.saving_paused': 'Saving is paused for maintenance until {time}. Please try again then.',
  'maintenance.saving_paused_shortly': 'Saving is paused for maintenance. Please try again in a few minutes.',
  'maintenance.screen_title': 'We’re upgrading NeonCarts — back shortly',
  'maintenance.screen_title_until': 'We’re upgrading NeonCarts — back by {time}',
  'maintenance.screen_body': 'Your store and its data are safe. This page checks every 30 seconds and returns to your dashboard on its own.',
  'maintenance.screen_check': 'Check now',
  // A capability the platform switched off during an incident: the sidebar
  // badge, the page that stands in for it, and the strip on pages it only
  // partly covers. `{feature}` is the page's name, `{time}` as above.
  'paused.badge': 'Paused',
  'paused.title': '{feature} is paused for a short while',
  'paused.back_by': 'Expected back by {time}.',
  'paused.back_shortly': 'We expect it back shortly.',
  'paused.body': 'We are fixing a problem. Your data is safe and nothing has been lost. This page checks every minute and comes back on its own.',
  'paused.check': 'Check now',
};

export default system;
